/**
 * recipes.ts — queue #664 §UAT-2026-07-09-R664 §R664-2.
 *
 * 대표 시나리오 6종의 **정적 모듈 조합(레시피)** 정의 + 순수 파생 함수. 실전 신규 프로젝트는
 * 항상 모듈 하나가 아니라 '조합'으로 시작하는데(파일 업로드 = 파일+검증+감사 등) 카탈로그는
 * 개별 모듈 단위 진입만 제공해 왔다 — 본 파일은 목적 지향 조합 가이드를 신규 데이터
 * 원천 0 으로 채운다(전체 실존 모듈 코드·실존 데모 라우트를 엮은 정적 정의).
 *
 * ★재사용·순수파생 원칙(§R640 facet-predicates.ts · §R643 maturity.ts · §R658 recent-changes.ts
 * 계보 답습) — `recipeModuleCodes` 는 `take-this.json[code].deps.codes`(§R639 BFS 전이 SSOT)를
 * union 할 뿐 FE 재계산하지 않는다. `mergeRecipeYml` 은 `TakeThisPanel` 의 기존 yml 렌더 규칙
 * (alwaysOn 스킵 · gateKey/andGates→`: true` · relatedConfig 원문)을 다중 코드로 확장한 파생이다
 * (신규 데이터 원천 0).
 *
 * ★i18n 분리 — 사람이 읽는 텍스트(name/summary/module role/step title·desc)는 전체 i18n 키
 * (ko/en 대칭). `snippet` 만 정적 코드 문자열(로케일 무관).
 * ★시한폭탄 없음 — 레시피 데이터·파생은 일자 의존 0(정적 조합). `now` 주입 규약 해당 없음.
 * ★재구현 금지 — 레시피는 구성 모듈의 공개 API 호출 순서만 문서화한다. 자체 파일 저장/
 * 이벤트 버스/캐시/리포트 로직 0줄(전체 공통 위임) — "무엇을 어떤 순서로 부르는가"의 정적
 * 가이드일 뿐 실행 코드가 아니다.
 */
import type { TakeThisData } from '../types/showcase';

/** 레시피 구성 모듈 1건 — 실존 코드 + 역할 라벨 i18n 키 */
export interface RecipeModuleRef {
  code: string; // 실존 모듈 코드(take-this.json 키) — 예 'C009'
  roleKey: string; // showcase.recipe.<id>.module.<code>.role i18n 키(조합 내 역할)
}

/** 단계별 wiring 스텝 — 제목·설명·코드 스니펫(코드 스니펫은 예시·복붙 가능) */
export interface RecipeStep {
  titleKey: string; // showcase.recipe.<id>.step.<n>.title
  descKey: string; // showcase.recipe.<id>.step.<n>.desc
  snippet: string; // Java/yml 스니펫(정적 문자열 · i18n 대상 아님 — 코드)
}

/** 대표 시나리오 레시피 */
export interface Recipe {
  id: string; // kebab-case 안정 id(딥링크 键) — 예 'file-pipeline'
  nameKey: string; // showcase.recipe.<id>.name
  summaryKey: string; // showcase.recipe.<id>.summary(1줄 목적)
  modules: RecipeModuleRef[]; // 구성 모듈(실존 코드)
  steps: RecipeStep[]; // 단계별 wiring
  demoRoutes: string[]; // 실존 딥링크('/showcase#C009' 또는 '/saga-monitor')
  relatedBiz?: string; // (선택) 대표 채택 biz 라우트
}

/** 합성 yml 1줄(gate 또는 relatedConfig 원문) — `mergeRecipeYml` 렌더용 얇은 래퍼. */
export interface RecipeYmlLine {
  text: string;
}

function toYmlLine(text: string): RecipeYmlLine {
  return { text };
}

/**
 * 레시피 구성 모듈 + 전이 폐쇄(take-this deps.codes) union → 정렬·dedup 코드집합.
 * §R641-2 계보: 전이 SSOT 는 take-this.json, FE 재계산 금지 — 여기서도 deps.codes 만 union.
 */
export function recipeModuleCodes(recipe: Recipe, takeThisData: TakeThisData): string[] {
  const set = new Set<string>();
  for (const m of recipe.modules) {
    set.add(m.code);
    for (const dep of takeThisData[m.code]?.deps.codes ?? []) set.add(dep);
  }
  return [...set].sort();
}

/**
 * 구성 모듈들의 take-this yml 을 병합 → 합성 yml 프로퍼티 목록(신규 원천 0 · dedup).
 * alwaysOn 항목은 게이트 불요(스킵) · opt-in(alwaysOn=false)만 gateKey/andGates=true + relatedConfig
 * 를 수집한다(`TakeThisPanel`/`buildYmlSnippet` 과 동일 규칙 · 레시피 다중 코드로 확장).
 * 전체 core(alwaysOn)인 레시피는 빈 배열을 반환 — 호출부가 "추가 설정 불요" 안내로 대체.
 */
export function mergeRecipeYml(recipe: Recipe, takeThisData: TakeThisData): RecipeYmlLine[] {
  const gates = new Set<string>(); // '<gateKey>: true'
  const related = new Set<string>(); // relatedConfig 원문(예 'spring.rabbitmq.host: localhost')
  for (const m of recipe.modules) {
    const yml = takeThisData[m.code]?.yml;
    if (!yml || yml.alwaysOn) continue; // 상시 on = 게이트 불요
    if (yml.gateKey) gates.add(`${yml.gateKey}: true`);
    for (const g of yml.andGates ?? []) gates.add(`${g}: true`);
    for (const rc of yml.relatedConfig ?? []) related.add(rc);
  }
  return [...[...gates].sort(), ...[...related].sort()].map(toYmlLine);
}

// ─── 대표 레시피 6종 (실존 코드·라우트만 — §R664-1 실측 대조 완료) ────────────────

export const RECIPES: Recipe[] = [
  {
    id: 'file-pipeline',
    nameKey: 'showcase.recipe.file-pipeline.name',
    summaryKey: 'showcase.recipe.file-pipeline.summary',
    modules: [
      { code: 'C009', roleKey: 'showcase.recipe.file-pipeline.module.C009.role' },
      { code: 'C003', roleKey: 'showcase.recipe.file-pipeline.module.C003.role' },
      { code: 'C014', roleKey: 'showcase.recipe.file-pipeline.module.C014.role' },
    ],
    steps: [
      {
        titleKey: 'showcase.recipe.file-pipeline.step.1.title',
        descKey: 'showcase.recipe.file-pipeline.step.1.desc',
        snippet: `FileService.upload(file, FileMeta.of("category"));`,
      },
      {
        titleKey: 'showcase.recipe.file-pipeline.step.2.title',
        descKey: 'showcase.recipe.file-pipeline.step.2.desc',
        snippet: `record UploadRequest(@SafeFileName String fileName, @NoXss String description) {}\n\nValidationFacade.validate(req);`,
      },
      {
        titleKey: 'showcase.recipe.file-pipeline.step.3.title',
        descKey: 'showcase.recipe.file-pipeline.step.3.desc',
        snippet: `@Audited(action = AuditAction.CUSTOM, customAction = "FILE_UPLOADED")\npublic FileUploadResponse upload(MultipartFile file) { ... }`,
      },
    ],
    demoRoutes: ['/showcase#C009', '/showcase#C003'],
  },
  {
    id: 'event-integration',
    nameKey: 'showcase.recipe.event-integration.name',
    summaryKey: 'showcase.recipe.event-integration.summary',
    modules: [
      { code: 'C008', roleKey: 'showcase.recipe.event-integration.module.C008.role' },
      { code: 'C056', roleKey: 'showcase.recipe.event-integration.module.C056.role' },
      { code: 'C067', roleKey: 'showcase.recipe.event-integration.module.C067.role' },
    ],
    steps: [
      {
        titleKey: 'showcase.recipe.event-integration.step.1.title',
        descKey: 'showcase.recipe.event-integration.step.1.desc',
        snippet: `eventPublisher.publishImmediate(new OrderPlacedEvent(orderId));`,
      },
      {
        titleKey: 'showcase.recipe.event-integration.step.2.title',
        descKey: 'showcase.recipe.event-integration.step.2.desc',
        snippet: `# application.yml\neraf.messaging.outbox.enabled: true\neraf.messaging.kafka.enabled: true\n\noutboxPublisher.enqueue(OutboxRecord.of(topic, key, payload));`,
      },
      {
        titleKey: 'showcase.recipe.event-integration.step.3.title',
        descKey: 'showcase.recipe.event-integration.step.3.desc',
        snippet: `sagaOrchestrator.run(new OrderSagaDefinition(orderId));`,
      },
    ],
    demoRoutes: ['/showcase#C008', '/showcase#C056', '/saga-monitor'],
  },
  {
    id: 'auth-gating',
    nameKey: 'showcase.recipe.auth-gating.name',
    summaryKey: 'showcase.recipe.auth-gating.summary',
    modules: [
      { code: 'C028', roleKey: 'showcase.recipe.auth-gating.module.C028.role' },
      { code: 'C030', roleKey: 'showcase.recipe.auth-gating.module.C030.role' },
      { code: 'C029', roleKey: 'showcase.recipe.auth-gating.module.C029.role' },
    ],
    steps: [
      {
        titleKey: 'showcase.recipe.auth-gating.step.1.title',
        descKey: 'showcase.recipe.auth-gating.step.1.desc',
        snippet: `// 자동 적용 — 컨트롤러 코드 0줄 (JwtAuthenticationFilter 가 Bearer 토큰 검증)`,
      },
      {
        titleKey: 'showcase.recipe.auth-gating.step.2.title',
        descKey: 'showcase.recipe.auth-gating.step.2.desc',
        snippet: `@HasPermission("user:read")\npublic ApiResponse<UserDto> getUser(@PathVariable String id) { ... }`,
      },
      {
        titleKey: 'showcase.recipe.auth-gating.step.3.title',
        descKey: 'showcase.recipe.auth-gating.step.3.desc',
        snippet: `PathPolicy.authenticated("/api/eraf/users/**")`,
      },
    ],
    demoRoutes: ['/showcase#C030', '/showcase#C028'],
  },
  {
    id: 'batch-notify',
    nameKey: 'showcase.recipe.batch-notify.name',
    summaryKey: 'showcase.recipe.batch-notify.summary',
    modules: [
      { code: 'C064', roleKey: 'showcase.recipe.batch-notify.module.C064.role' },
      { code: 'C065', roleKey: 'showcase.recipe.batch-notify.module.C065.role' },
      { code: 'C073', roleKey: 'showcase.recipe.batch-notify.module.C073.role' },
    ],
    steps: [
      {
        titleKey: 'showcase.recipe.batch-notify.step.1.title',
        descKey: 'showcase.recipe.batch-notify.step.1.desc',
        snippet: `@ErafScheduled(cron = "0 0 * * * *")\npublic void runNightlyBatch() { ... }`,
      },
      {
        titleKey: 'showcase.recipe.batch-notify.step.2.title',
        descKey: 'showcase.recipe.batch-notify.step.2.desc',
        snippet: `batchJobRunner.run(NightlyReconcileJob.of(batchId));`,
      },
      {
        titleKey: 'showcase.recipe.batch-notify.step.3.title',
        descKey: 'showcase.recipe.batch-notify.step.3.desc',
        snippet: `notificationSender.sendEmail(EmailMessage.of(recipient, "배치 완료", body));`,
      },
    ],
    demoRoutes: ['/scheduler-monitor', '/notification-center'],
  },
  {
    id: 'cache-guard',
    nameKey: 'showcase.recipe.cache-guard.name',
    summaryKey: 'showcase.recipe.cache-guard.summary',
    modules: [
      { code: 'C051', roleKey: 'showcase.recipe.cache-guard.module.C051.role' },
      { code: 'C050', roleKey: 'showcase.recipe.cache-guard.module.C050.role' },
      { code: 'C017', roleKey: 'showcase.recipe.cache-guard.module.C017.role' },
    ],
    steps: [
      {
        titleKey: 'showcase.recipe.cache-guard.step.1.title',
        descKey: 'showcase.recipe.cache-guard.step.1.desc',
        snippet: `@Cacheable("product-catalog")\npublic ProductDto findProduct(String id) { ... }`,
      },
      {
        titleKey: 'showcase.recipe.cache-guard.step.2.title',
        descKey: 'showcase.recipe.cache-guard.step.2.desc',
        snippet: `# application.yml — L1 캐시 미스 시 자동 폴백\nspring.cache.type: redis`,
      },
      {
        titleKey: 'showcase.recipe.cache-guard.step.3.title',
        descKey: 'showcase.recipe.cache-guard.step.3.desc',
        snippet: `@RateLimiter(name = "productClient")\npublic ProductDto callUpstream(String id) { ... }`,
      },
    ],
    demoRoutes: ['/cache-admin', '/showcase#C017'],
  },
  {
    id: 'report-export',
    nameKey: 'showcase.recipe.report-export.name',
    summaryKey: 'showcase.recipe.report-export.summary',
    modules: [
      { code: 'C082', roleKey: 'showcase.recipe.report-export.module.C082.role' },
      { code: 'C078', roleKey: 'showcase.recipe.report-export.module.C078.role' },
      { code: 'C079', roleKey: 'showcase.recipe.report-export.module.C079.role' },
    ],
    steps: [
      {
        titleKey: 'showcase.recipe.report-export.step.1.title',
        descKey: 'showcase.recipe.report-export.step.1.desc',
        snippet: `ReportDefinition.of("monthly-sales", columns, rows);`,
      },
      {
        titleKey: 'showcase.recipe.report-export.step.2.title',
        descKey: 'showcase.recipe.report-export.step.2.desc',
        snippet: `excelExporter.write(response.getOutputStream(), rows, ExcelExportOptions.defaults());`,
      },
      {
        titleKey: 'showcase.recipe.report-export.step.3.title',
        descKey: 'showcase.recipe.report-export.step.3.desc',
        snippet: `pdfGenerator.generate(response.getOutputStream(), title, columns, rows);`,
      },
    ],
    demoRoutes: ['/showcase#C078', '/showcase#C082', '/showcase#C079'],
  },
];
